import {
  type CompanyNotificationSettingsInput,
  type CompanyNotifications,
  type CompanySettings,
  DIGEST_HOURS,
  type MaintenanceInput,
  type SiteReportDeadlineView,
  TIMEZONES,
  type UpdateNoticesAdmin,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { DeliveryList } from '../../components/NotificationsPanel';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { BUNDLE_COMMIT, checkNow } from '../../lib/updates';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { DeadlineControlPanel } from './DeadlineControlPanel';
import { MailReviewPanel } from './MailReviewPanel';
import { RegulationsPanel } from './RegulationsPanel';
import { ReleaseMailPanel } from './ReleaseMailPanel';

const KEY = ['admin', 'settings'];
const NOTICES_KEY = ['admin', 'update-notices'];
const NOTIFY_KEY = ['admin', 'notifications'];
const SITE_DEADLINE_KEY = ['admin', 'site-report-deadline'];

/**
 * Настройки компании (АД-08): окно проверки дробления закупок (ЗК-33), уведомления сотрудникам, предупреждения
 * об обновлении и письма «Портал обновлён» (АД-09)
 */
export function SettingsPage() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api<CompanySettings>('/admin/settings') });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return (
    <div className="flex flex-col gap-5">
      <SplitWindow current={q.data.splitWindowDays} limit={q.data.threeOffersLimit} />
      <CompanyNotificationsPanel />
      <DeadlineControlPanel />
      <MailReviewPanel />
      <RegulationsPanel />
      <SiteReportDeadlinePanel />
      <UpdateNotices />
      <ReleaseMailPanel />
    </div>
  );
}

const SERVICE_TITLES: Record<string, string> = { api: 'сервер', worker: 'фоновые задачи', web: 'страницы' };

const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;
const DEADLINE_HOURS = Array.from({ length: 24 }, (_, i) => i);

/**
 * Срок сдачи отчёта смены (ПР-05): час по поясу компании; после срока несданные участки — в «Ждёт меня».
 */
function SiteReportDeadlinePanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: SITE_DEADLINE_KEY,
    queryFn: () => api<SiteReportDeadlineView>('/site/report-deadline'),
  });
  const [hour, setHour] = useState<number | null>(null);
  useEffect(() => {
    if (q.data && hour === null) setHour(q.data.hour);
  }, [q.data, hour]);
  const save = useMutation({
    mutationFn: (body: { hour: number }) =>
      api<SiteReportDeadlineView>('/site/report-deadline', { method: 'PUT', body }),
    onSuccess: (x) => {
      qc.setQueryData(SITE_DEADLINE_KEY, x);
      setHour(x.hour);
      void qc.invalidateQueries({ queryKey: ['site'] });
      toast('Срок сдачи отчёта сохранён');
    },
  });
  return (
    <Panel title="Срок сдачи отчёта смены" meta="ПР-05; часовой пояс — как у уведомлений компании">
      <div className="flex max-w-2xl flex-col gap-4 text-[14px]">
        <p className="text-ink-2">
          Прораб сдаёт отчёт смены по каждому выданному участку до выбранного часа. После срока несданные
          участки появляются в «Ждёт меня» у прораба и у руководителя проекта списком.
        </p>
        {q.isPending || hour === null ? (
          q.isError ? (
            <ErrorBox error={q.error} />
          ) : (
            <Spinner />
          )
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : (
          <>
            <Field label="Час сдачи">
              <Select value={String(hour)} onChange={(e) => setHour(Number(e.target.value))}>
                {DEADLINE_HOURS.map((h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </Select>
            </Field>
            <p className="text-[13px] text-ink-2">
              Пояс компании: {q.data.timezone}. Сейчас{' '}
              {q.data.pastDeadline ? 'срок на сегодня уже прошёл' : 'ещё есть время сдать отчёт'}.
            </p>
            <ErrorBox error={save.error} />
            <div>
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={hour === q.data.hour}
                onClick={() => save.mutate({ hour })}
              >
                Сохранить
              </Button>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

/**
 * Уведомления сотрудникам (УВ-05, УВ-07, УВ-09): пояс и час утренней сводки по умолчанию, состояние почты и
 * бота Telegram без секретов, сколько сотрудников получают сводку и журнал доставки — недоставленное первым.
 */
function CompanyNotificationsPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: NOTIFY_KEY,
    queryFn: () => api<CompanyNotifications>('/admin/notifications'),
  });
  // Пояс в форме — строка: сервер может прислать пояс вне списка выбора (BUSINESS_TZ стенда)
  const [form, setForm] = useState<CompanyNotifications['settings'] | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(q.data.settings);
  }, [q.data, form]);
  const save = useMutation({
    mutationFn: (body: CompanyNotificationSettingsInput) =>
      api<CompanyNotifications>('/admin/notifications', { method: 'PUT', body }),
    onSuccess: (x) => {
      qc.setQueryData(NOTIFY_KEY, x);
      setForm(x.settings);
      void qc.invalidateQueries({ queryKey: ['notifications', 'me'] });
      toast('Настройки уведомлений сохранены');
    },
  });
  return (
    <Panel title="Уведомления сотрудникам" meta="раздел 6.20; изменения — в «Истории изменений»">
      <div className="flex max-w-2xl flex-col gap-4 text-[14px]">
        <p className="text-ink-2">
          Каждое утро сотрудники получают сводку своих дел из «Ждёт меня» и «Что нового» на почту и в
          Telegram. Каждый сам выбирает каналы и час в своём профиле; здесь — час по умолчанию и часовой пояс
          компании. Суммы в сводку не попадают.
        </p>
        {q.isPending || !form ? (
          q.isError ? (
            <ErrorBox error={q.error} />
          ) : (
            <Spinner />
          )
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : (
          <>
            <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[200px_1fr]">
              <dt className="text-ink-2">Почта</dt>
              <dd>
                {q.data.email.configured
                  ? `настроена, отправитель ${q.data.email.from}`
                  : 'не настроена — нужны SMTP_URL и MAIL_FROM в настройках сервера'}
              </dd>
              <dt className="text-ink-2">Бот Telegram</dt>
              <dd>
                {q.data.telegram.configured
                  ? `${q.data.telegram.botUsername ? `@${q.data.telegram.botUsername}` : 'настроен'}${
                      q.data.telegram.webhookOk === false ? ' — приём сообщений не настроен (webhook)' : ''
                    }`
                  : 'не настроен — нужен TELEGRAM_BOT_TOKEN в настройках сервера'}
              </dd>
              <dt className="text-ink-2">Получают сводку</dt>
              <dd>
                {q.data.settings.digestEnabled ? (
                  <>
                    {q.data.recipients.total} чел.: почта — {q.data.recipients.email}, Telegram —{' '}
                    {q.data.recipients.telegram}
                  </>
                ) : (
                  'никто — сводка выключена для компании'
                )}
              </dd>
            </dl>
            <Checkbox
              label="Утренняя сводка сотрудникам компании"
              description="Выключите, пока сводку не нужно присылать никому из компании, например на время запуска портала. Пробную сводку из профиля каждый может прислать себе и при выключенной."
              checked={form.digestEnabled}
              onChange={(e) => setForm({ ...form, digestEnabled: e.target.checked })}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Часовой пояс компании">
                <Select
                  value={form.timezone}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      timezone: e.target.value as CompanyNotificationSettingsInput['timezone'],
                    })
                  }
                >
                  {Object.entries(TIMEZONES).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                  {form.timezone in TIMEZONES ? null : <option value={form.timezone}>{form.timezone}</option>}
                </Select>
              </Field>
              <Field label="Час сводки по умолчанию">
                <Select
                  value={String(form.digestHour)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      digestHour: Number(e.target.value) as CompanyNotificationSettingsInput['digestHour'],
                    })
                  }
                >
                  {DIGEST_HOURS.map((h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <ErrorBox error={save.error} />
            <div>
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={JSON.stringify(form) === JSON.stringify(q.data.settings)}
                onClick={() => save.mutate(form as CompanyNotificationSettingsInput)}
              >
                Сохранить
              </Button>
            </div>
            <section className="border-t border-line pt-4">
              <h3 className="mb-2 text-[13px] font-medium text-ink">Журнал доставки</h3>
              <DeliveryList items={q.data.deliveries} withName />
            </section>
          </>
        )}
      </div>
    </Panel>
  );
}

/**
 * Предупреждения об обновлении (АД-09): последняя выкладка из CI — её объявляет сам CI за 5 минут, — и
 * предупреждение сотрудникам компании о работах, которое ставит администратор.
 */
function UpdateNotices() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: NOTICES_KEY,
    queryFn: () => api<UpdateNoticesAdmin>('/admin/update-notices'),
  });
  const [inMinutes, setInMinutes] = useState('30');
  const [duration, setDuration] = useState('30');
  const [message, setMessage] = useState('');
  const done = (x?: UpdateNoticesAdmin) => {
    if (x) qc.setQueryData(NOTICES_KEY, x);
    else qc.invalidateQueries({ queryKey: NOTICES_KEY });
    void checkNow();
  };
  const set = useMutation({
    mutationFn: (body: MaintenanceInput) =>
      api<UpdateNoticesAdmin>('/admin/maintenance', { method: 'PUT', body }),
    onSuccess: (x) => {
      done(x);
      setMessage('');
      toast('Сотрудники предупреждены');
    },
  });
  const clear = useMutation({
    mutationFn: () => api('/admin/maintenance', { method: 'DELETE' }),
    onSuccess: () => {
      done();
      toast('Предупреждение снято');
    },
  });
  const tooLong = message.length > 200;
  return (
    <Panel title="Предупреждения об обновлении" meta="АД-09; изменения — в «Истории изменений»">
      <div className="flex max-w-2xl flex-col gap-4 text-[14px]">
        <p className="text-ink-2">
          За 5 минут до каждой выкладки новой версии портал сам предупреждает всех: плашка с отсчётом, во
          время выкладки — «Идёт обновление», после — «Вышла новая версия, обновите страницу». Сам портал
          страницу не перезагружает, поэтому введённое не пропадает.
        </p>
        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : (
          <>
            <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[200px_1fr]">
              <dt className="text-ink-2">Работающая версия</dt>
              <dd className="num">
                сервер {q.data.commit}, эта страница {BUNDLE_COMMIT}
                {BUNDLE_COMMIT !== 'local' && q.data.commit !== 'local' && BUNDLE_COMMIT !== q.data.commit
                  ? ' — обновите страницу'
                  : ''}
              </dd>
              <dt className="text-ink-2">Последняя выкладка</dt>
              <dd>
                {q.data.release ? (
                  <>
                    версия <span className="num">{q.data.release.commit}</span> (
                    {q.data.release.services.map((x) => SERVICE_TITLES[x] ?? x).join(', ')}), начало{' '}
                    {dateTime(q.data.release.startsAt)}
                  </>
                ) : (
                  'за последние 30 минут выкладок не было'
                )}
              </dd>
            </dl>
            <section className="flex flex-col gap-3 border-t border-line pt-4">
              <h3 className="text-[14px] font-semibold text-ink">Работы на портале</h3>
              {q.data.maintenance ? (
                <div className="flex flex-col gap-2 rounded-md bg-warning-soft px-3 py-2.5">
                  <p>
                    Сотрудники видят: работы с {dateTime(q.data.maintenance.startsAt)} до{' '}
                    {dateTime(q.data.maintenance.endsAt)}
                    {q.data.maintenance.message ? ` — ${q.data.maintenance.message}` : ''}
                    {q.data.maintenance.createdByName ? `. Автор: ${q.data.maintenance.createdByName}` : ''}
                  </p>
                  <div>
                    <Button variant="secondary" loading={clear.isPending} onClick={() => clear.mutate()}>
                      Снять предупреждение
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-ink-2">
                  Предупредите сотрудников компании о плановых работах, например о переносе базы: плашка
                  появится у всех, кто вошёл в портал.
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Когда начнутся">
                  <Select value={inMinutes} onChange={(e) => setInMinutes(e.target.value)}>
                    <option value="0">Уже идут</option>
                    <option value="5">Через 5 минут</option>
                    <option value="15">Через 15 минут</option>
                    <option value="30">Через 30 минут</option>
                    <option value="60">Через час</option>
                    <option value="120">Через 2 часа</option>
                    <option value="1440">Через сутки</option>
                  </Select>
                </Field>
                <Field label="Сколько продлятся">
                  <Select value={duration} onChange={(e) => setDuration(e.target.value)}>
                    <option value="15">15 минут</option>
                    <option value="30">30 минут</option>
                    <option value="60">Час</option>
                    <option value="120">2 часа</option>
                    <option value="240">4 часа</option>
                  </Select>
                </Field>
              </div>
              <Field
                label="Что будет (необязательно)"
                error={tooLong ? 'Не длиннее 200 знаков' : undefined}
                hint="Например: перенос базы на новый сервер"
              >
                <Input value={message} onChange={(e) => setMessage(e.target.value)} invalid={tooLong} />
              </Field>
              <div>
                <Button
                  variant="primary"
                  loading={set.isPending}
                  disabled={tooLong}
                  onClick={() =>
                    set.mutate({
                      inMinutes: Number(inMinutes),
                      durationMinutes: Number(duration),
                      message: message.trim() || undefined,
                    })
                  }
                >
                  {q.data.maintenance ? 'Заменить предупреждение' : 'Предупредить сотрудников'}
                </Button>
              </div>
              <ErrorBox error={set.error ?? clear.error} />
            </section>
          </>
        )}
      </div>
    </Panel>
  );
}

function SplitWindow({ current, limit }: { current: number; limit: number }) {
  const qc = useQueryClient();
  const [days, setDays] = useState(String(current));
  const [lim, setLim] = useState(String(limit));
  const n = Number(days);
  const l = Number(lim.replace(/\s/g, ''));
  const valid = Number.isInteger(n) && n >= 7 && n <= 365;
  const validLim = lim.trim() !== '' && Number.isInteger(l) && l >= 0 && l <= 1e12;
  const save = useMutation({
    mutationFn: () =>
      api<CompanySettings>('/admin/settings', {
        method: 'PUT',
        body: { splitWindowDays: n, threeOffersLimit: l },
      }),
    onSuccess: (x) => {
      qc.setQueryData(KEY, x);
      qc.invalidateQueries({ queryKey: ['contracts'] });
      qc.invalidateQueries({ queryKey: ['procurement'] });
      toast('Настройки закупок сохранены');
    },
  });
  return (
    <Panel title="Порог трёх КП и контроль дробления" meta="ЗК-05, ЗК-33; изменения — в «Истории изменений»">
      <div className="flex max-w-2xl flex-col gap-4 text-[14px]">
        <p className="text-ink-2">
          Закупка выше порога требует не меньше трёх КП или обоснования без трёх КП. Закупка до порога
          помечается до согласования, если у того же контрагента за окно проверки до или после неё есть
          сходные договоры или заявки тоже до порога, а вместе они выше порога. Пометку видят согласующие и
          служба безопасности.
        </p>
        <Field
          label="Порог трёх КП, ₸"
          error={validLim ? undefined : 'Целое число от 0'}
          className="max-w-xs"
        >
          <Input inputMode="numeric" value={lim} onChange={(e) => setLim(e.target.value)} />
        </Field>
        <Field
          label="Окно проверки, дней"
          error={valid ? undefined : 'От 7 до 365 дней'}
          className="max-w-xs"
        >
          <Input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.trim())} />
        </Field>
        <div>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!valid || !validLim || (n === current && l === limit)}
            onClick={() => save.mutate()}
          >
            Сохранить
          </Button>
        </div>
        <ErrorBox error={save.error} />
      </div>
    </Panel>
  );
}
